import React, { useEffect, useState } from 'react';
import { Shield, Search, Target, AlertTriangle, FileCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const Pentest = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section with Parallax */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1563206767-5b18f218e8de?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.5}px)`,
            willChange: 'transform'
          }}
        />
        <div className="absolute inset-0 opacity-15 bg-[linear-gradient(rgba(11,112,42,0.25)_1px,transparent_1px),linear-gradient(90deg,rgba(11,112,42,0.25)_1px,transparent_1px)] bg-[size:50px_50px]" style={{ transform: `translateY(${scrollY * 0.25}px)`, willChange: 'transform' }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-[#0C9F39]/40 to-black" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#014421]/80 backdrop-blur-sm rounded-full border border-[#0B702A]/30 mb-8">
            <Shield className="w-4 h-4 text-[#0B702A]" />
            <span className="text-sm">{t.pentest.badge}</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-6">{t.pentest.title}</h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t.pentest.subtitle}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.pentest.whatIncludes}</h2>
            <p className="text-xl text-gray-400">{t.pentest.whatIncludesSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6">
                <Search className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.pentest.features.analysis.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.pentest.features.analysis.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.pentest.features.invasion.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.pentest.features.invasion.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6">
                <FileCheck className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.pentest.features.report.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.pentest.features.report.description}</p>
            </div>

            <div className="p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:bg-[#014421]/50 hover:border-[#0B702A]/60 transition-all duration-300 hover:scale-105">
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6">
                <AlertTriangle className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{t.pentest.features.risk.title}</h3>
              <p className="text-gray-400 leading-relaxed">{t.pentest.features.risk.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology with Parallax */}
      <section className="relative py-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.2}px) scale(${1 + scrollY * 0.0001})`,
            willChange: 'transform'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#014421]/20 to-black" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">{t.pentest.methodology}</h2>
            <p className="text-xl text-gray-400">{t.pentest.methodologySubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#0B702A]/20 rounded-full flex items-center justify-center border-2 border-[#0B702A]">
                <span className="text-3xl font-bold text-[#0B702A]">01</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">{t.pentest.steps.recon.title}</h3>
              <p className="text-gray-400">{t.pentest.steps.recon.description}</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#0B702A]/20 rounded-full flex items-center justify-center border-2 border-[#0B702A]">
                <span className="text-3xl font-bold text-[#0B702A]">02</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">{t.pentest.steps.mapping.title}</h3>
              <p className="text-gray-400">{t.pentest.steps.mapping.description}</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#0B702A]/20 rounded-full flex items-center justify-center border-2 border-[#0B702A]">
                <span className="text-3xl font-bold text-[#0B702A]">03</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">{t.pentest.steps.exploitation.title}</h3>
              <p className="text-gray-400">{t.pentest.steps.exploitation.description}</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#0B702A]/20 rounded-full flex items-center justify-center border-2 border-[#0B702A]">
                <span className="text-3xl font-bold text-[#0B702A]">04</span>
              </div>
              <h3 className="text-2xl font-bold mb-3">{t.pentest.steps.documentation.title}</h3>
              <p className="text-gray-400">{t.pentest.steps.documentation.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.pentest.ctaTitle}</h2>
          <p className="text-xl text-gray-400 mb-10">{t.pentest.ctaSubtitle}</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-5 bg-[#0B702A] text-white text-lg font-semibold rounded-lg hover:bg-[#0C9F39] transition-all duration-300 shadow-lg hover:shadow-[#0B702A]/50 hover:scale-105"
          >
            {t.pentest.ctaButton}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Pentest;